import { ApiSchema } from "@api-types";
import { styleTokens } from "@ideally-ltd/design-tokens";
import { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { getFFService } from "services/ff.service";

export namespace Globals {
  export const StaticImageBaseUrl = "https://static.goideally.com/images/webapp/";
  export const RegionOther = 999999;
  export const BackendServicesUserId = 5380;
  export const MinSampleSize = 30;

  export const ALLOWED_BRIEF_FILE_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  export const ALLOWED_STIMULUS_FILE_TYPES = [
    "image/png",
    "image/gif",
    "image/jpg",
    "image/jpeg",
    "video/mp4",
    "video/quicktime",
    "audio/mpeg",
    "audio/mpeg3",
  ];

  export const ALL_ALLOWED_FILE_TYPES = [...ALLOWED_BRIEF_FILE_TYPES, ...ALLOWED_STIMULUS_FILE_TYPES];

  export const ALLOWED_STIMULUS_FILE_TYPES_EXT = [".png", ".gif", ".jpg", ".jpeg", ".mp4", ".mov", ".mp3"];

  export const ALLOWED_FILE_TYPES_EXT = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".png",
    ".gif",
    ".jpg",
    ".jpeg",
    ".mp4",
    ".mov",
    ".mp3",
  ];

  export const MAX_FILE_SIZE_MB = 50;

  export const getFileCategory = (
    mimeType: string,
  ): { fileType: "Brief" | "Stimulus"; contextLabel: string; icon: string } =>
    ALLOWED_BRIEF_FILE_TYPES.includes(mimeType)
      ? { fileType: "Brief", contextLabel: "brief", icon: "file_copy" }
      : { fileType: "Stimulus", contextLabel: "stimulus", icon: "image" };
  export type TestOptionFile = ExistingFile | DeleteFile | LocalFile | UploadedFile;

  export type ModuleNames =
    | "results"
    | "admin"
    | "compare"
    | "enterprise-management"
    | "generic"
    | "org-management"
    | "team-management"
    | "onboarding"
    | "profile"
    | "groups"
    | "main"
    | "user-home";
  export interface ExistingFile {
    type: "existing";
    url: string;
    contentType: string;
    isThumbnail?: boolean;
  }

  export interface DeleteFile {
    type: "delete";
  }

  export interface LocalFile {
    type: "local";
    file: File;
  }

  export interface UploadedFile {
    type: "uploaded";
    key: string;
    contentType: string;
  }

  export interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics?: any;
  }

  export enum ThumbnailTypes {
    Square = 1,
    Ratio = 2,
  }

  export enum ThemingVersions {
    StandardTheming = 1,
    SentimentTheming = 2,
    SentimentThemingGrouped = 3,
    NonEmotional = 5,
  }

  export enum CategoryTypeLookup {
    Product = 1,
    Service = 2,
  }

  //make a type that takes a T|undefined and returns T
  export type NonNullable<T> = T extends undefined ? never : T;

  export enum SystemQuestions {
    HddStructure = 37,
    Ethnicity = 41,
    Region = 42,
    TechAdop = 43,
    Gender = 45,
    HddIncome = 46,
    Generation = 60,
    Age = 51,
  }

  export enum StandardQuestions {
    PurchaseIntent = 3,
    WhatYouLikeMost = 7,
    ReasonsToTry = 14,
    Levers = 18,
    BrandRecognition = 6,
    BrandFit = 2,
    Distinctiveness = 4,
    Desire = 1,
    NPDAware = 9,
    InstoreVisibility = 11,
    AdvertisementOuttake = 10,
    NPDTried = 8,
    Experience = 15,
    Repeat = 16,
    Likeability = 5,
    BarriersToRepeat = 17,
    BarriersToTry = 13,
    TooCheap = 19,
    Cheap = 20,
    Expensive = 21,
    TooExpensive = 22,
    Consideration = 23,
    Believability = 24,
    MarketReach = 25,
    PurchaseIntentV2 = 26,
  }

  export enum AiEvaluationResultTypes {
    Fail = -1,
    Pass = 1,
  }

  /** Maximum respondent context blocks allowed per project. Mirrored by MaxContentsPerProject. */
  export const MaxContentsPerProject = 5;

  export enum QuestionPositions {
    Screener = 1,
    InsideLoop = 2,
    OutsideLoop = 3,
    Awareness = 4,
    Standard = 5,
    Custom = 6,
    BeforeLoop = 7,
    Segment = 9,
    Occasions = 10,
    UsageAndAttitudeTracked = 13,
    CustomMetric = 14,
    OccasionsNet = 15,
  }

  export const QuestionPositionSurveyOrder = (questionPositionId: QuestionPositions) => {
    const positionOrder = [
      QuestionPositions.Screener,
      QuestionPositions.BeforeLoop,
      QuestionPositions.Awareness,
      QuestionPositions.Standard,
      QuestionPositions.CustomMetric,
      QuestionPositions.Custom,
      QuestionPositions.Occasions,
      QuestionPositions.InsideLoop,
      QuestionPositions.UsageAndAttitudeTracked,
      QuestionPositions.OutsideLoop,
      QuestionPositions.OccasionsNet,
    ];
    const index = positionOrder.indexOf(questionPositionId);
    if (index >= 0) return index;
    throw new Error(`Unhandled question position: ${questionPositionId}`);
  };

  /**
   * Positions whose survey build renders respondent context blocks. Anywhere else a block would
   * save but silently never reach the respondent. Mirrored by SupportsRespondentContext on the
   * backend, which rejects the write - keep the two in step.
   */
  export const SupportsRespondentContext = (questionPositionId: QuestionPositions) =>
    questionPositionId === QuestionPositions.Screener ||
    questionPositionId === QuestionPositions.InsideLoop ||
    questionPositionId === QuestionPositions.OutsideLoop;

  export const QuestionPositionsMapped: { [key in ApiSchema.QuestionPositions]: QuestionPositions } = {
    Screener: QuestionPositions.Screener,
    InsideLoop: QuestionPositions.InsideLoop,
    OutsideLoop: QuestionPositions.OutsideLoop,
    Awareness: QuestionPositions.Awareness,
    Standard: QuestionPositions.Standard,
    Custom: QuestionPositions.Custom,
    BeforeLoop: QuestionPositions.BeforeLoop,
    Segment: QuestionPositions.Segment,
    Occasions: QuestionPositions.Occasions,
    UsageAndAttitudeTracked: QuestionPositions.UsageAndAttitudeTracked,
    CustomMetric: QuestionPositions.CustomMetric,
    OccasionsNet: QuestionPositions.OccasionsNet,
  } as const;
  export const QuestionPositionsMappedBack: { [key in QuestionPositions]: ApiSchema.QuestionPositions } = {
    [QuestionPositions.Screener]: "Screener",
    [QuestionPositions.InsideLoop]: "InsideLoop",
    [QuestionPositions.OutsideLoop]: "OutsideLoop",
    [QuestionPositions.Awareness]: "Awareness",
    [QuestionPositions.Standard]: "Standard",
    [QuestionPositions.Custom]: "Custom",
    [QuestionPositions.BeforeLoop]: "BeforeLoop",
    [QuestionPositions.Segment]: "Segment",
    [QuestionPositions.Occasions]: "Occasions",
    [QuestionPositions.UsageAndAttitudeTracked]: "UsageAndAttitudeTracked",
    [QuestionPositions.CustomMetric]: "CustomMetric",
    [QuestionPositions.OccasionsNet]: "OccasionsNet",
  } as const;

  export enum UAndATypes {
    OtherCore = 1,
    Occasions = 2,
    NeedsRational = 3,
    NeedsEmotional = 4,
    Brand = 5,
    SubBrandOrType = 6,
    SpendOrVolume = 7,
    FrequencyOfPurchase = 8,
    Adjacency = 9,
    Provider = 10,
    SubCategory = 11,
    Price = 30,
    HealthAndLifestyleAttitudes = 31,
    MediaConsumption = 32,
    BrandChoiceBehaviour = 33,
    Stores = 34,
    Barriers = 35,
    Workarounds = 36,
    LikelihoodToSwitch = 37,
    SwitchingBarriers = 38,
    CurrentInclusions = 39,
    PriorityInclusions = 40,
    HandsetBrand = 41,
    DeviceInfluence = 43,
    AttitudeTowardsNewOffers = 44,
    DiscoveryChannels = 45,
    OtherDiscovery = 999,
  }

  export const getUAndATypeLabel = (uAndATypeId: UAndATypes) => {
    switch (uAndATypeId) {
      case UAndATypes.Occasions:
        return "Occasions";
      case UAndATypes.NeedsRational:
        return "Rational needs";
      case UAndATypes.NeedsEmotional:
        return "Emotional needs";
      case UAndATypes.Brand:
        return "Brands";
      case UAndATypes.SpendOrVolume:
        return "Spend";
      case UAndATypes.FrequencyOfPurchase:
        return "Frequency";
      case UAndATypes.OtherCore:
        return "Other";
      default:
        return UAndATypes[uAndATypeId];
    }
  };

  export enum BenchmarkTypes {
    IdeallyAverage = 1,
    ClientAverage = 2,
    ProductTypeAverage = 3,
    WeekIdeallyAverage0_8 = 4,
    WeekIdeallyAverage9_16 = 5,
    WeekIdeallyAverage17_25 = 6,
    WeekIdeallyAverage26Plus = 7,
    CategoryAverage = 8,
    ParentCategoryAverage = 10,
  }

  export const isTeamBenchmark = (benchmarkTypeId?: number) =>
    [BenchmarkTypes.ClientAverage].includes(benchmarkTypeId ?? 0);

  export const getBenchmarkDescription = (benchmarkId: number) => {
    switch (benchmarkId) {
      case BenchmarkTypes.IdeallyAverage:
        return "Benchmark performance across all customers and historical data.";
      case BenchmarkTypes.ClientAverage:
        return "Benchmark performance across tests in your team.";
      case BenchmarkTypes.ProductTypeAverage:
        return "Benchmark performance across tests within your industry.";
      case BenchmarkTypes.WeekIdeallyAverage0_8:
        return "Benchmark performance across new product launches in their first 8 weeks.";
      case BenchmarkTypes.WeekIdeallyAverage9_16:
        return "Benchmark performance across new product launches during weeks 9 to 16.";
      case BenchmarkTypes.WeekIdeallyAverage17_25:
        return "Benchmark performance across new product launches during weeks 17 to 25.";
      case BenchmarkTypes.WeekIdeallyAverage26Plus:
        return "Benchmark performance across new product launches from week 26 onward.";
      case BenchmarkTypes.CategoryAverage:
      case BenchmarkTypes.ParentCategoryAverage:
        return "Benchmark performance across tests within this category.";
    }
  };

  export enum AiSummaryTypes {
    ListViewSummary = 1,
    MapViewSummary = 2,
    TestGroupPerformanceSummary = 3,
    DriversSummary = 6,
    ProfileQuestion = 13,
    FullTestSummary = 14,
    QualQuestionSummary = 16,
    DriversSummaryByTheme = 17,
    ListViewSummaryMultiple = 18,
    MapViewSummaryMultiple = 19,
    EmailSummaryIdea = 20,
    EmailSummaryMessage = 21,
    EmailSummaryNpd = 22,
    EmailSummaryQuestion = 23,
    PriceSensitivitySummary = 24,
  }

  export function getAiSummaryTypeValue(name: keyof typeof AiSummaryTypes): number {
    return AiSummaryTypes[name];
  }

  export enum AiProjectGroupSummaryTypes {
    SegmentOverview = 1,
    OccasionBySegment = 2,
    UandAByOccasion = 3,
    OccasionsOverview = 4,
    UandABySegment = 5,
    UandAByDemandZone = 6,
  }

  export function getAiProjectGroupSummaryTypeValue(name: keyof typeof AiProjectGroupSummaryTypes): number {
    return AiProjectGroupSummaryTypes[name];
  }

  export enum CreditLogTypes {
    HubspotDeal = 1,
    CBSubscription = 2,
    PendingSurveyRun = 30,
    SurveyRun = 40,
    ServProjectLoad = 50,
    ServReporting = 51,
    ServAnalysis = 52,
    AddRunAdj = 80,
    StdQuestionAdj = 81,
    AddProfilerAdj = 82,
    MakeGoodAdj = 83,
    BoostedSampleAdj = 84,
    MonadicAdj = 85,
    Freebie = 86,
    OtherAdj = 90,
    PendingTransfer = 130,
    Transfer = 140,
  }

  export function getProjectSpecificCreditLogTypes(): CreditLogTypes[] {
    return [
      CreditLogTypes.ServProjectLoad,
      CreditLogTypes.ServReporting,
      CreditLogTypes.ServAnalysis,
      CreditLogTypes.AddRunAdj,
      CreditLogTypes.StdQuestionAdj,
      CreditLogTypes.AddProfilerAdj,
      CreditLogTypes.MakeGoodAdj,
      CreditLogTypes.BoostedSampleAdj,
      CreditLogTypes.MonadicAdj,
      CreditLogTypes.OtherAdj,
    ];
  }

  export enum ProjectTypes {
    Idea = 1,
    Message = 2,
    Question = 3,
    Npd = 4,
  }

  export enum ProjectLogEventTypes {
    Created = 1,
    StatusChanged = 2,
    Deleted = 3,
    ResultsReady = 4,
    Shared = 5,
    SubmittedForSurvey = 6,
    ResearcherApproved = 7,
    ResearcherRejected = 8,
    ResearcherCleared = 9,
  }

  export enum QuestionType {
    single = 1,
    multiple = 2,
    text = 3,
    dropdown = 4,
    numeric = 5,
  }
  export const QuestionTypeToDisplay = (questionTypeId: QuestionType) => {
    switch (questionTypeId) {
      case QuestionType.single:
        return "Single choice";
      case QuestionType.multiple:
        return "Multiple choice";
      case QuestionType.text:
        return "Open end";
      case QuestionType.dropdown:
        return "Dropdown";
      case QuestionType.numeric:
        return "Numeric";
    }
  };

  export enum ProjectStatusTypes {
    Draft = 0,
    AwaitingApproval = 50,
    Submitted = 100,
    ReviewingQuestions = 200, // deprecated
    AwaitingSurvey = 300,
    WaitingOnResponses = 400,
    SomeResultsReady = 600,
    CompletedWave = 800, // deprecated
    Complete = 900,
    Demo = 950,
  }

  export enum ProjectScheduleTypes {
    Standard = 1,
    DelayCharge = 10,
    DelayChargeFuture = 15,
  }

  export enum PromptTypes {
    DriversByTheme = 1,
    DriversOverall = 2,
    EmailIdea = 3,
    EmailMessage = 4,
    EmailNpd = 5,
    EmailQuestion = 6,
    ListViewMultipleTests = 7,
    ListViewSingleTest = 8,
    MapViewMultipleTests = 9,
    MapViewSingleTest = 10,
    TestGroupPerformance = 12,
    ProfileQuestion = 13,
    IncidenceEstimate = 14,
    FullTestSummary = 15,
    QualQuestionSummary = 16,
    PriceSensitivitySummary = 18,
    ImageFileMetadata = 19,
    ThemeGenerate = 20,
    ThemeTextResponses = 21,
    ShortThemeSummary = 23,
    AssignProjectCategory = 25,
    ProjectTranslation = 26,
    StandardQuestionTranslation = 27,
    DecipherSurveyTextTranslation = 28,
    CategorySegments = 29,
    TextResponsesQa = 31,
    SentimentThemeGenerate = 33,
    SentimentThemeTextResponses = 34,
    DocumentToProject = 35,
    ProjectPdfFileMetadata = 36,
    ProjectGroupSegmentSummary = 38,
    OccasionsQuestionSummary = 39,
    NeedsQuestionSummary = 40,
    SentimentThemeGroup = 41,
    ResearchBuilder = 42,
    SyntheticSurveyWorkflow = 44,
    Theming = 45,
    OccasionNetsGeneration = 46,
    DemandZonesWorkflow = 47,
    ProjectGroupSummaryWorkflow = 48,
    BulkInviteWorkflow = 49,
    FactsFindingsWorkflow = 65,
    SlideDeckGeneration = 70,
    BackfillProjectObjectives = 100,
    DiscoverProductsWorkflow = 1000,
    RespondentsCsvEditWorkflow = 1001,
    GenerateFilterSetsWorkflow = 1008,
  }

  export const ProjectStatusLabels: { [key in ProjectStatusTypes]: string } = {
    [ProjectStatusTypes.Draft]: "Draft",
    [ProjectStatusTypes.AwaitingApproval]: "Awaiting Approval",
    [ProjectStatusTypes.Submitted]: "Submitted",
    [ProjectStatusTypes.ReviewingQuestions]: "Reviewing Questions",
    [ProjectStatusTypes.AwaitingSurvey]: "Awaiting Survey",
    [ProjectStatusTypes.WaitingOnResponses]: "Processing results",
    [ProjectStatusTypes.SomeResultsReady]: "Some Results Available",
    [ProjectStatusTypes.CompletedWave]: "Completed Wave",
    [ProjectStatusTypes.Complete]: "Complete",
    [ProjectStatusTypes.Demo]: "Demo",
  };

  export enum Frameworks {
    IdeaDefault = 1000,
    InnovationTerritoryTest = 1001,
    CopyAndClaimsTest = 1002,
    ExecutionABTest = 1003,
    PriceSensitivity = 1010,
    MarketReach = 1020,
    IdeaCustomMetric = 1200,
    MessageComprehensionTest = 2000,
    StrategicTerritoryTest = 2001,
    CreativeCommsTest = 2002,
    ABTest = 2003,
    MessageCustomMetric = 2200,
    Adhoc = 3000,
    NPD = 4000,
    UsageAndAttitudeSeeding = 3998,
  }

  export enum SurveyStatuses {
    Draft = 0,
    Testing = 10,
    Live = 50,
    Closed = 90,
    Dev = 130,
    ErrorXml = 1000,
    ErrorProcessing = 1010,
  }

  export enum SurveySampleProviders {
    Ideally = 1,
    Customer = 2,
    Simulated = 3,
    Synthetic = 4,
  }

  export enum SyntheticPanelScopes {
    Project = 1,
    ProjectGroup = 2,
    Category = 3,
    Ideally = 4,
  }

  export const getSyntheticPanelScopeName = (scope: number): string => {
    switch (scope) {
      case SyntheticPanelScopes.Project:
        return "Project";
      case SyntheticPanelScopes.ProjectGroup:
        return "Canvas";
      case SyntheticPanelScopes.Category:
        return "Category";
      case SyntheticPanelScopes.Ideally:
        return "Ideally";
      default:
        return String(scope);
    }
  };

  export enum SyntheticPanelStatuses {
    Generating = 1,
    Active = 2,
    Degraded = 3,
    Failed = 4,
    Stale = 5,
  }

  export const SyntheticPanelStatusSeverity: Record<string, "jade" | "warning" | "danger" | "secondary"> = {
    Active: "jade",
    Generating: "warning",
    Degraded: "warning",
    Stale: "secondary",
    Failed: "danger",
  };

  export enum KnowledgeDocumentIngestionStatuses {
    Pending = 0,
    Parsing = 1,
    Chunking = 2,
    Embedding = 3,
    Indexing = 4,
    Partial = 5,
    Failed = 6,
    Success = 7,
    Deriving = 8,
  }

  export const KnowledgeDocumentIngestionStatusLabels: {
    [key in KnowledgeDocumentIngestionStatuses]: string;
  } = {
    [KnowledgeDocumentIngestionStatuses.Pending]: "Pending",
    [KnowledgeDocumentIngestionStatuses.Parsing]: "Parsing",
    [KnowledgeDocumentIngestionStatuses.Chunking]: "Chunking",
    [KnowledgeDocumentIngestionStatuses.Embedding]: "Embedding",
    [KnowledgeDocumentIngestionStatuses.Indexing]: "Indexing",
    [KnowledgeDocumentIngestionStatuses.Partial]: "Partial",
    [KnowledgeDocumentIngestionStatuses.Failed]: "Failed",
    [KnowledgeDocumentIngestionStatuses.Success]: "Success",
    [KnowledgeDocumentIngestionStatuses.Deriving]: "Deriving",
  };

  export const KnowledgeDocumentIngestionStatusSeverity: {
    [key in KnowledgeDocumentIngestionStatuses]: "jade" | "warning" | "danger" | "secondary";
  } = {
    [KnowledgeDocumentIngestionStatuses.Pending]: "secondary",
    [KnowledgeDocumentIngestionStatuses.Parsing]: "warning",
    [KnowledgeDocumentIngestionStatuses.Chunking]: "warning",
    [KnowledgeDocumentIngestionStatuses.Embedding]: "warning",
    [KnowledgeDocumentIngestionStatuses.Indexing]: "warning",
    [KnowledgeDocumentIngestionStatuses.Partial]: "warning",
    [KnowledgeDocumentIngestionStatuses.Failed]: "danger",
    [KnowledgeDocumentIngestionStatuses.Success]: "jade",
    [KnowledgeDocumentIngestionStatuses.Deriving]: "warning",
  };

  export const KnowledgeDocumentIngestionTerminalStatuses: KnowledgeDocumentIngestionStatuses[] = [
    KnowledgeDocumentIngestionStatuses.Partial,
    KnowledgeDocumentIngestionStatuses.Failed,
    KnowledgeDocumentIngestionStatuses.Success,
  ];

  export enum KnowledgeDocumentRetrievalStatuses {
    Pending = 0,
    Running = 1,
    Completed = 2,
    Failed = 3,
  }

  export const KnowledgeDocumentRetrievalTerminalStatuses: KnowledgeDocumentRetrievalStatuses[] = [
    KnowledgeDocumentRetrievalStatuses.Completed,
    KnowledgeDocumentRetrievalStatuses.Failed,
  ];

  export enum KnowledgeDatasetIngestionStatuses {
    Pending = 0,
    Converting = 1,
    Profiling = 2,
    Indexing = 3,
    Failed = 4,
    Success = 5,
  }

  export const KnowledgeDatasetIngestionStatusLabels: {
    [key in KnowledgeDatasetIngestionStatuses]: string;
  } = {
    [KnowledgeDatasetIngestionStatuses.Pending]: "Pending",
    [KnowledgeDatasetIngestionStatuses.Converting]: "Converting",
    [KnowledgeDatasetIngestionStatuses.Profiling]: "Profiling",
    [KnowledgeDatasetIngestionStatuses.Indexing]: "Indexing",
    [KnowledgeDatasetIngestionStatuses.Failed]: "Failed",
    [KnowledgeDatasetIngestionStatuses.Success]: "Success",
  };

  export const KnowledgeDatasetIngestionStatusSeverity: {
    [key in KnowledgeDatasetIngestionStatuses]: "jade" | "warning" | "danger" | "secondary";
  } = {
    [KnowledgeDatasetIngestionStatuses.Pending]: "secondary",
    [KnowledgeDatasetIngestionStatuses.Converting]: "warning",
    [KnowledgeDatasetIngestionStatuses.Profiling]: "warning",
    [KnowledgeDatasetIngestionStatuses.Indexing]: "warning",
    [KnowledgeDatasetIngestionStatuses.Failed]: "danger",
    [KnowledgeDatasetIngestionStatuses.Success]: "jade",
  };

  export const KnowledgeDatasetIngestionTerminalStatuses: KnowledgeDatasetIngestionStatuses[] = [
    KnowledgeDatasetIngestionStatuses.Failed,
    KnowledgeDatasetIngestionStatuses.Success,
  ];

  export enum KnowledgeDatasetOrigins {
    Sheet = 1,
    EmbeddedTable = 2,
  }

  export const KnowledgeDatasetOriginLabels: {
    [key in KnowledgeDatasetOrigins]: string;
  } = {
    [KnowledgeDatasetOrigins.Sheet]: "Sheet",
    [KnowledgeDatasetOrigins.EmbeddedTable]: "Embedded table",
  };

  export enum KnowledgeDatasetQueryStatuses {
    Pending = 0,
    Planning = 1,
    Executing = 2,
    Interpreting = 3,
    Failed = 4,
    Success = 5,
  }

  export const KnowledgeDatasetQueryStatusLabels: {
    [key in KnowledgeDatasetQueryStatuses]: string;
  } = {
    [KnowledgeDatasetQueryStatuses.Pending]: "Pending",
    [KnowledgeDatasetQueryStatuses.Planning]: "Planning",
    [KnowledgeDatasetQueryStatuses.Executing]: "Running query",
    [KnowledgeDatasetQueryStatuses.Interpreting]: "Interpreting",
    [KnowledgeDatasetQueryStatuses.Failed]: "Failed",
    [KnowledgeDatasetQueryStatuses.Success]: "Answered",
  };

  export const KnowledgeDatasetQueryStatusSeverity: {
    [key in KnowledgeDatasetQueryStatuses]: "jade" | "warning" | "danger" | "secondary";
  } = {
    [KnowledgeDatasetQueryStatuses.Pending]: "secondary",
    [KnowledgeDatasetQueryStatuses.Planning]: "warning",
    [KnowledgeDatasetQueryStatuses.Executing]: "warning",
    [KnowledgeDatasetQueryStatuses.Interpreting]: "warning",
    [KnowledgeDatasetQueryStatuses.Failed]: "danger",
    [KnowledgeDatasetQueryStatuses.Success]: "jade",
  };

  export const KnowledgeDatasetQueryTerminalStatuses: KnowledgeDatasetQueryStatuses[] = [
    KnowledgeDatasetQueryStatuses.Failed,
    KnowledgeDatasetQueryStatuses.Success,
  ];

  export enum ABFrameworks {
    ExecutionABTest = 1003,
    MessageABTest = 2003,
  }

  export const isAb = (frameworkId: number) =>
    [Globals.ABFrameworks.ExecutionABTest, Globals.ABFrameworks.MessageABTest].includes(frameworkId);

  export const isSeed = (frameworkId: number) =>
    [Globals.Frameworks.UsageAndAttitudeSeeding].includes(frameworkId);

  export const ProjectTypeToFrameworkId = (projectType: ApiSchema.ProjectTypeLookup | null): number => {
    switch (projectType) {
      case "idea":
        return 1000;
      case "message":
        return 2000;
      case "question":
        return 3000;
      case "npd":
        return 4000;
      default:
        return 1000;
    }
  };
  export const ProjectTypeToDisplay = (projectType?: string | null): string => {
    switch (projectType) {
      case "idea":
        return "idea";
      case "message":
        return "message";
      default:
        return "product";
    }
  };

  export const ProjectTypeIdToProjectType = (projectTypeId: number): ApiSchema.ProjectTypeLookup => {
    switch (projectTypeId) {
      case 1:
        return "idea";
      case 2:
        return "message";
      case 3:
        return "question";
      case 4:
        return "npd";
      default:
        return "idea";
    }
  };

  export const ProjectTypeIdToDisplay = (projectTypeId?: number | null): string => {
    switch (projectTypeId) {
      case 1:
        return "idea";
      case 2:
        return "message";
      case 3:
        return "question";
      default:
        return "product";
    }
  };

  export const ProjectTypeToNoun = (projectType?: string | null, capitalise = false): string => {
    const noun = projectType === "npd" ? "product" : projectType || "idea";
    return capitalise ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)}` : noun;
  };

  export const supportsObjectiveAiSummariesTypes: ApiSchema.AiSummaryTypes[] = [
    "ListViewSummary",
    "ListViewSummaryMultiple",
    "MapViewSummary",
    "MapViewSummaryMultiple",
    // "QualQuestionSummary",
    // "DriversSummary",
    // "DriversSummaryByTheme",
    // "EmailSummaryIdea",
    // "EmailSummaryMessage",
    // "EmailSummaryNpd",
    // "EmailSummaryQuestion",
    // "FullTestSummary",
    // "PriceSensitivitySummary",
  ];

  export const getStandardQuestionLabel = (id: number) => {
    switch (id) {
      case 1:
        return "Desire";
      case 2:
        return "Brand Fit";
      case 3:
        return "Purchase intent";
      case 4:
        return "Distinctiveness";
      case 5:
        return "Likability";
      case 6:
        return "Brand Recognition";
      case 7:
        return "What you like most";
      case 8:
        return "Trial";
      case 9:
        return "Recognition";
      case 10:
        return "Advertisement Outtake";
      case 11:
        return "In-store visibility";
      case 17:
        return "Repeat barriers";
      case 18:
        return "Repeat drivers";
      case 13:
        return "Trial barriers";
      case 14:
        return "Trial drivers";
      case 15:
        return "Positive experience";
      case 16:
        return "Repeat purchase";
      case 19:
        return "Too Cheap";
      case 20:
        return "Bargain";
      case 21:
        return "Expensive";
      case 22:
        return "Too Expensive";
      case 23:
        return "Consideration";
      case 24:
        return "Believability";
      case 26:
        return "Purchase intent";
      default:
        return "Untitled";
    }
  };

  export const getQuestionPositionText = (questionPositionId: QuestionPositions) => {
    switch (questionPositionId) {
      case Globals.QuestionPositions.Screener:
        return "Screener";
      case Globals.QuestionPositions.Awareness:
        return "Awareness";
      case Globals.QuestionPositions.BeforeLoop:
        return "Profile";
      case Globals.QuestionPositions.InsideLoop:
        return "Test";
      case Globals.QuestionPositions.OutsideLoop:
        return "Follow-up";
      case Globals.QuestionPositions.Standard:
        return "Framework";
      case Globals.QuestionPositions.Custom:
        return "Custom";
      case Globals.QuestionPositions.Occasions:
        return "Occasions";
      case Globals.QuestionPositions.UsageAndAttitudeTracked:
        return "Usage & attitude";
      case Globals.QuestionPositions.CustomMetric:
        return "Custom metric";
      default:
        return "";
    }
  };

  export const getStandardQuestionValue = (answer: number) => {
    switch (answer) {
      case 1:
        return "High";
      case 2:
        return "Average";
      case 3:
        return "Neutral";
      case 4:
        return "Low";
      default:
        return null;
    }
  };

  export const isPricingSensitivityQuestion = (questionId: number) => {
    if (
      [
        StandardQuestions.TooCheap,
        StandardQuestions.Cheap,
        StandardQuestions.Expensive,
        StandardQuestions.TooExpensive,
      ].includes(questionId)
    ) {
      return true;
    }
  };

  export const isAddOnQuestion = (questionId: number) => {
    if (isPricingSensitivityQuestion(questionId)) {
      return true;
    }
    if (questionId === StandardQuestions.MarketReach) {
      return true;
    }
    return false;
  };

  export const OptionalMetrics = [StandardQuestions.BrandFit, StandardQuestions.BrandRecognition];

  export const getStandardQuestionColour = (id: number) => {
    switch (id) {
      case StandardQuestions.Desire:
        return styleTokens.Chart.petrel[3];
      case StandardQuestions.BrandFit:
        return styleTokens.Chart.ochre[5];
      case StandardQuestions.PurchaseIntent:
      case StandardQuestions.PurchaseIntentV2:
      case StandardQuestions.Consideration:
        return styleTokens.Chart.coral[9];
      case StandardQuestions.Distinctiveness:
        return styleTokens.Chart.candy[1];
      case StandardQuestions.Likeability:
        return styleTokens["Ideally-Core"].petrel[60];
      case StandardQuestions.BrandRecognition:
        return styleTokens.Chart.petrel[8];

      // NPD
      case StandardQuestions.NPDAware:
        return styleTokens.Chart.petrel[4];
      case StandardQuestions.InstoreVisibility:
        return styleTokens.Chart.petrel[3];
      case StandardQuestions.NPDTried:
        return styleTokens.Chart.petrel[2];
      case StandardQuestions.Experience:
        return styleTokens.Chart.candy[1];
      case StandardQuestions.Repeat:
        return styleTokens.Chart.candy[9];
      case StandardQuestions.Believability:
        return styleTokens.Chart.core[8];
      default:
        return styleTokens.Surface.Text.sub;
    }
  };

  export const getRandomQuestionColour = (num: number) => {
    // Collect color swatches by family
    const petrel = Object.values(styleTokens.Chart.petrel).filter((a) => a && typeof a === "string");
    const coral = Object.values(styleTokens.Surface.Coral).filter((a) => a && typeof a === "string");
    const candy = Object.values(styleTokens.Chart.candy).filter((a) => a && typeof a === "string");
    const scarlet = Object.values(styleTokens.Chart.scarlet).filter((a) => a && typeof a === "string");
    const ochre = Object.values(styleTokens.Chart.ochre).filter((a) => a && typeof a === "string");
    const extras = [
      "#1C96FF",
      styleTokens.Surface.Text.sub,
      "#4FD8AE",
      "#009D9A",
      "#870E64",
      "#214100",
      "#6929C4",
      "#c2c2c2",
      "#3b3939",
    ].filter((a) => a !== "#ffffff");

    // Interleave color families to maximize color separation
    const maxLength = Math.max(petrel.length, coral.length, candy.length, scarlet.length, ochre.length);
    const interleaved = [];
    for (let i = 0; i < maxLength; i++) {
      if (petrel[i]) interleaved.push(petrel[i]!);
      if (coral[i]) interleaved.push(coral[i]!);
      if (candy[i]) interleaved.push(candy[i]!);
      if (scarlet[i]) interleaved.push(scarlet[i]!);
      if (ochre[i]) interleaved.push(ochre[i]!);
      if (extras[i]) interleaved.push(extras[i]!);
    }
    // Remove duplicates and #ffffff, final filter
    const colours = Array.from(new Set(interleaved)).filter((a) => a !== "#ffffff");
    return colours[num % colours.length]!;
  };
  const demandZoneColours = [
    styleTokens.Surface.Petrel.petrelPrimary,
    styleTokens.Surface.Candy.candyPrimary,
    styleTokens.Surface.Terracotta.citrusPrimary,
    styleTokens.Surface.Iris.irisPrimary,
    styleTokens.Surface.Coral.coralPrimary,
    styleTokens.Surface.Jade.jadePrimary,
    styleTokens.Surface.Scarlet.scarletPrimary,
  ];
  const lighterDemandZoneColours = [
    styleTokens.Surface.Petrel.petrelBright,
    "#FAE8F7",
    styleTokens.Surface.Terracotta.citrusBright,
    styleTokens.Surface.Iris.irisBright,
    "#FEDDE2",
    styleTokens.Surface.Jade.jadeBright,
    "#E9CED0",
  ];
  export const getDemandZoneColor = (demandZoneIndex: number, useLighterColor?: boolean) => {
    return useLighterColor
      ? lighterDemandZoneColours[demandZoneIndex % lighterDemandZoneColours.length]
      : demandZoneColours[demandZoneIndex % demandZoneColours.length];
  };

  function hex2(c: number) {
    c = Math.round(c);
    if (c < 0) c = 0;
    if (c > 255) c = 255;

    let s = c.toString(16);
    if (s.length < 2) s = "0" + s;

    return s;
  }

  function color(r: number, g: number, b: number) {
    return "#" + hex2(r) + hex2(g) + hex2(b);
  }

  export const lightenColour = (col: string, light: number = 0.5) => {
    // TODO: Assert that col is good and that -1 < light < 1

    let r = parseInt(col.substr(1, 2), 16);
    let g = parseInt(col.substr(3, 2), 16);
    let b = parseInt(col.substr(5, 2), 16);

    if (light < 0) {
      r = (1 + light) * r;
      g = (1 + light) * g;
      b = (1 + light) * b;
    } else {
      r = (1 - light) * r + light * 255;
      g = (1 - light) * g + light * 255;
      b = (1 - light) * b + light * 255;
    }

    return color(r, g, b);
  };
  // export const lightenColour = (col: string, amt: number = 128) => {
  //   let usePound = false;

  //   if (col[0] === "#") {
  //     col = col.slice(1);
  //     usePound = true;
  //   }

  //   const num = parseInt(col, 16);
  //   let r = (num >> 16) + amt;

  //   if (r > 255) r = 255;
  //   else if (r < 0) r = 0;

  //   let b = ((num >> 8) & 0x00ff) + amt;

  //   if (b > 255) b = 255;
  //   else if (b < 0) b = 0;

  //   let g = (num & 0x0000ff) + amt;

  //   if (g > 255) g = 255;
  //   else if (g < 0) g = 0;

  //   return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
  // };

  export const getRandomTestOptionColour = (testOptionIndex: number, contextId: number) => {
    function shuffleArray<T>(array: T[], seed: number): T[] {
      const random = () => {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [array[i], array[j]] = [array[j]!, array[i]!];
      }
      return array;
    }

    const chartColours = styleTokens.Chart;
    const colourGroupsByPriority = [
      [
        chartColours.petrel[3],
        chartColours.coral[9],
        chartColours.ochre[7],
        chartColours.core[8],
        chartColours.petrel[1],
      ],
      [
        chartColours.core[9],
        chartColours.scarlet[3],
        chartColours.core[10],
        chartColours.coral[4],
        chartColours.core[1],
      ],
      [
        chartColours.petrel[2],
        chartColours.coral[3],
        chartColours.ochre[9],
        chartColours.candy[6],
        chartColours.petrel[7],
      ],
      [
        chartColours.core[5],
        chartColours.core[11],
        chartColours.core[13],
        chartColours.candy[7],
        chartColours.candy[5],
      ],
      [
        chartColours.petrel[9],
        chartColours.coral[6],
        chartColours.ochre[6],
        chartColours.candy[8],
        chartColours.core[7],
      ],
      [
        chartColours.ochre[4],
        chartColours.scarlet[2],
        chartColours.core[2],
        chartColours.coral[5],
        chartColours.petrel[8],
      ],
      [
        chartColours.petrel[4],
        chartColours.coral[1],
        chartColours.ochre[5],
        chartColours.candy[3],
        chartColours.ochre[1],
      ],
      [
        chartColours.ochre[2],
        chartColours.scarlet[6],
        chartColours.petrel[5],
        chartColours.candy[9],
        chartColours.petrel[6],
      ],
    ];

    for (const [index, colourGroup] of colourGroupsByPriority.entries()) {
      shuffleArray(colourGroup, (contextId ?? 0) + index);
    }

    const combinedColours = colourGroupsByPriority.flat();
    return combinedColours[testOptionIndex % combinedColours.length];
  };

  export type ChartColourFamily = keyof typeof styleTokens.Chart;

  export const getChartColour = (index: number, family: ChartColourFamily) => {
    const colours: string[] = Object.values(styleTokens.Chart[family]).filter(
      (a) => a && typeof a === "string",
    );

    const shuffledColours = [...colours];
    const seed = 31528;

    let currentSeed = seed;
    const seededRandom = () => {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      return currentSeed / 233280;
    };

    for (let i = shuffledColours.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [shuffledColours[i], shuffledColours[j]] = [shuffledColours[j] as string, shuffledColours[i] as string];
    }

    const base = shuffledColours[index % shuffledColours.length] as string;
    const cycle = Math.floor(index / shuffledColours.length);
    if (!cycle) {
      return base;
    }

    const step = Math.ceil(cycle / 2) * 0.22;
    return lightenColour(base, cycle % 2 ? Math.min(step, 0.66) : -Math.min(step, 0.66));
  };

  export const getRandomPetrelColour = (num: number) => {
    const colours: string[] = Object.values(styleTokens.Chart.petrel).filter(
      (a) => a && typeof a === "string",
    );

    // Deterministic shuffle to separate similar colors while maintaining consistent order
    const shuffledColours = [...colours];
    const seed = 31528; // Fixed seed for consistent shuffling

    // Simple seeded random number generator
    let currentSeed = seed;
    const seededRandom = () => {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      return currentSeed / 233280;
    };

    // Fisher-Yates shuffle with seeded random
    for (let i = shuffledColours.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [shuffledColours[i], shuffledColours[j]] = [shuffledColours[j] as string, shuffledColours[i] as string];
    }
    return shuffledColours[num % shuffledColours.length];
  };

  export const getRandomQuestionColourByStr = (str: string) =>
    getRandomQuestionColour(str.split("").reduce((p, c) => p + c.charCodeAt(0), 0));

  const standardQuestionOrder: Record<number, number> = {
    [Globals.StandardQuestions.Likeability]: 1,
    [Globals.StandardQuestions.Desire]: 2,
    [Globals.StandardQuestions.Distinctiveness]: 3,
    [Globals.StandardQuestions.PurchaseIntent]: 4,
    [Globals.StandardQuestions.PurchaseIntentV2]: 4,
    [Globals.StandardQuestions.BrandFit]: 5,
    [Globals.StandardQuestions.BrandRecognition]: 6,
    [Globals.StandardQuestions.NPDAware]: 10,
    [Globals.StandardQuestions.InstoreVisibility]: 11,
    [Globals.StandardQuestions.NPDTried]: 12,
    [Globals.StandardQuestions.Experience]: 13,
    [Globals.StandardQuestions.Repeat]: 14,
  };

  export enum Regions {
    NZ = 1,
    AU = 2,
    UK = 14,
    US = 15,
    CN = 16,
    SG = 17,
    CA = 18,
    MX = 19,
    BR = 21,
    HK = 24,
    IN = 25,
    FR = 32,
    DE = 33,
    IT = 37,
    ZA = 44,
    ES = 45,
    SE = 46,
    ID = 48,
    JP = 49,
    MY = 50,
    PH = 51,
    KR = 52,
    TW = 53,
    TH = 54,
    VN = 55,
    US_AL = 56,
    US_AZ = 58,
    US_AR = 59,
    US_CA = 60,
    US_CO = 61,
    US_CT = 62,
    US_FL = 64,
    US_GA = 65,
    US_ID = 67,
    US_IL = 68,
    US_IN = 69,
    US_IA = 70,
    US_KS = 71,
    US_KY = 72,
    US_LA = 73,
    US_ME = 74,
    US_MD = 75,
    US_MA = 76,
    US_MI = 77,
    US_MN = 78,
    US_MS = 79,
    US_MO = 80,
    US_NE = 82,
    US_NV = 83,
    US_NH = 84,
    US_NJ = 85,
    US_NM = 86,
    US_NY = 87,
    US_NC = 88,
    US_OH = 90,
    US_OK = 91,
    US_OR = 92,
    US_PA = 93,
    US_RI = 94,
    US_SC = 95,
    US_TN = 97,
    US_TX = 98,
    US_UT = 99,
    US_VA = 101,
    US_WA = 102,
    US_WV = 103,
    US_WI = 104,
  }

  export const getTargetableRegions = (): {
    regionId: number;
    label: string;
    currency: string;
  }[] => [
    { regionId: 2, label: "Australia", currency: "AUD" },
    { regionId: 1, label: "New Zealand", currency: "NZD" },
    { regionId: 15, label: "USA", currency: "USD" },
    { regionId: 17, label: "Singapore", currency: "SGD" },
    { regionId: Globals.RegionOther, label: "Other markets (UK, SE Asia etc.)", currency: "USD" },
    ...(getFFService().featureEnabled("CanChooseUsStateMarkets")
      ? [
          { regionId: 56, label: "Alabama", currency: "USD" },
          { regionId: 58, label: "Arizona", currency: "USD" },
          { regionId: 59, label: "Arkansas", currency: "USD" },
          { regionId: 60, label: "California", currency: "USD" },
          { regionId: 61, label: "Colorado", currency: "USD" },
          { regionId: 62, label: "Connecticut", currency: "USD" },
          { regionId: 64, label: "Florida", currency: "USD" },
          { regionId: 65, label: "Georgia", currency: "USD" },
          { regionId: 67, label: "Idaho", currency: "USD" },
          { regionId: 68, label: "Illinois", currency: "USD" },
          { regionId: 69, label: "Indiana", currency: "USD" },
          { regionId: 70, label: "Iowa", currency: "USD" },
          { regionId: 71, label: "Kansas", currency: "USD" },
          { regionId: 72, label: "Kentucky", currency: "USD" },
          { regionId: 73, label: "Louisiana", currency: "USD" },
          { regionId: 74, label: "Maine", currency: "USD" },
          { regionId: 75, label: "Maryland", currency: "USD" },
          { regionId: 76, label: "Massachusetts", currency: "USD" },
          { regionId: 77, label: "Michigan", currency: "USD" },
          { regionId: 78, label: "Minnesota", currency: "USD" },
          { regionId: 79, label: "Mississippi", currency: "USD" },
          { regionId: 80, label: "Missouri", currency: "USD" },
          { regionId: 82, label: "Nebraska", currency: "USD" },
          { regionId: 83, label: "Nevada", currency: "USD" },
          { regionId: 84, label: "New Hampshire", currency: "USD" },
          { regionId: 85, label: "New Jersey", currency: "USD" },
          { regionId: 86, label: "New Mexico", currency: "USD" },
          { regionId: 87, label: "New York", currency: "USD" },
          { regionId: 88, label: "North Carolina", currency: "USD" },
          { regionId: 90, label: "Ohio", currency: "USD" },
          { regionId: 91, label: "Oklahoma", currency: "USD" },
          { regionId: 92, label: "Oregon", currency: "USD" },
          { regionId: 93, label: "Pennsylvania", currency: "USD" },
          { regionId: 94, label: "Rhode Island", currency: "USD" },
          { regionId: 95, label: "South Carolina", currency: "USD" },
          { regionId: 97, label: "Tennessee", currency: "USD" },
          { regionId: 98, label: "Texas", currency: "USD" },
          { regionId: 99, label: "Utah", currency: "USD" },
          { regionId: 101, label: "Virginia", currency: "USD" },
          { regionId: 102, label: "Washington", currency: "USD" },
          { regionId: 103, label: "West Virginia", currency: "USD" },
          { regionId: 104, label: "Wisconsin", currency: "USD" },
        ]
      : []),
  ];
  export const getStandardQuestionOrder = (id: number) => standardQuestionOrder[id] ?? 7 + id;

  export const getSurveySampleProviderId = (panelType: ApiSchema.PanelType) => {
    switch (panelType) {
      case "Ideally":
        return 1;
      case "Customer":
        return 2;
      case "Simulated":
        return 3;
      case "Synthetic":
        return 4;
    }
  };
  export const getPanelType: (panelType: number) => ApiSchema.PanelType = (panelType) => {
    switch (panelType) {
      case 1:
        return "Ideally";
      case 2:
        return "Customer";
      case 3:
        return "Simulated";
      case 4:
        return "Synthetic";
      default:
        return "Ideally";
    }
  };

  export enum StandardQuestionAnswerValues {
    Top = 1,
    SecondTop = 2,
    SecondBottom = 3,
    Bottom = 4,
  }

  export const DefaultObjectiveText =
    "Summarize the overall performance and highlight any significant audience skews in the results.";

  export const IndustryOptions = [
    { label: "Telecommunications", value: "telecommunications" },
    { label: "Energy & Utilities", value: "energy" },
    { label: "Broadcast Media", value: "broadcast" },
    { label: "QSR", value: "qsr" },
    { label: "Agency", value: "agency" },
    { label: "Financial Services", value: "financial" },
    { label: "Government", value: "government" },
    { label: "Other", value: "other" },
    { label: "Retail & Supermarkets", value: "retail" },
    { label: "Insurance", value: "insurance" },
    { label: "FMCG", value: "fmcg" },
    { label: "Tech", value: "tech" },
    { label: "Health/Pharma", value: "health" },
    { label: "Hospitality, Travel & Tourism", value: "hospitality" },
  ];

  export const pricingSensitivityPriceTooltips = {
    optimal: "The sweet spot. The price that feels just right, not too cheap or expensive.",
    indifferent:
      "Neutral ground. People are willing to pay this, but it doesn’t feel like a deal or a stretch.",
    acceptable:
      "What feels fair. Most people would buy at this price - it’s seen as trustworthy and good value.",
  };

  export enum ProjectCommentTypes {
    Basic = 1,
    ResearcherRequest = 2,
  }

  export enum UserClientRoles {
    Admin = 1,
    User = 2,
    Viewer = 3,
  }

  export enum UserRoles {
    SuperAdmin = 1,
    Research = 2,
    Sales = 3,
    Viewer = 4,
    User = 100,
  }

  export const getUserClientRoleLabel = (roleId: UserClientRoles) => {
    switch (roleId) {
      case UserClientRoles.Admin:
        return "Admin";
      case UserClientRoles.User:
        return "User";
      case UserClientRoles.Viewer:
        return "Viewer";
    }
  };

  export const AlwaysShownDemographicFilters = ["AGE_EXACT", "GENDER", "HH_STR_SH_PROC", "dRegion"];

  export const ClientTierOrder: Record<ApiSchema.ClientTierLookup, number> = {
    Enterprise: 0,
    Org: 1,
    Team: 2,
  };

  export const PasswordRules: Omit<
    RegisterOptions<FieldValues, FieldPath<FieldValues>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  > = {
    required: "Please enter a new password",
    minLength: {
      value: 10,
      message:
        "Password must be at least 10 characters, including an uppercase letter, a lowercase letter, and a number.",
    },
    validate: (value) => {
      const errors = [];
      if (!value.match(/[A-Z]/)) {
        errors.push("one uppercase letter");
      }
      if (!value.match(/[a-z]/)) {
        errors.push("one lowercase letter");
      }
      if (!value.match(/[0-9]/)) {
        errors.push("one number");
      }
      if (errors.length > 0) {
        return `Password must contain at least ${errors.join(", ")}.`;
      }
      return true;
    },
  };

  export enum ProjectFactTypes {
    QualResult = 1,
    MetricResult = 2,
    ThemeResult = 3,
    DriverResult = 4,
    MarketReachResult = 5,
    PricingSenseResult = 6,
  }

  export const getProjectFactTypeIcon = (factTypeId: number): string => {
    switch (factTypeId) {
      case ProjectFactTypes.MetricResult:
        return "trophy";
      case ProjectFactTypes.QualResult:
        return "insert_chart";
      case ProjectFactTypes.ThemeResult:
        return "reviews";
      case ProjectFactTypes.DriverResult:
        return "rocket_launch";
      case ProjectFactTypes.MarketReachResult:
        return "groups";
      case ProjectFactTypes.PricingSenseResult:
        return "sell";
      default:
        return "insights";
    }
  };

  export enum ProjectFindingTypes {
    BehaviouralPrevalence = 1,
    UsageContext = 2,
    PerceptionAndDefinition = 3,
    ForwardLookingOpportunities = 4,
    CrossMarketComparison = 5,
    QualitativeDrivers = 6,
    SegmentDifferences = 7,
    Barriers = 8,
    BenchmarkPerformance = 9,
    RegressionDrivers = 10,
    CrossOptionComparison = 11,
    PerformanceOverview = 12,
    BrandImpactAndConsideration = 13,
    NamingOrFormatPreference = 14,
    MessageRecallAndTakeaway = 15,
    ClaimPerformance = 16,
    RepeatPurchaseInsights = 17,
    BundleIdentification = 18,
    SegmentSpecificBundles = 19,
    PricingOptimumAndRange = 20,
    PricingSegmentDifferences = 21,
  }

  export const FrameworkSelectedMessage =
    "Framework has been selected. Setting up the survey based on the current plan and methodology.";

  export const getFrequencyLabel = (frequency: number | null | undefined) => {
    if (frequency === undefined || frequency === null) return "Not set";
    switch (frequency) {
      case 1:
        return "Weekly";
      case 2:
        return "Fortnightly";
      case 4:
        return "Every 4 weeks";
      case 13:
        return "Quarterly";
      case 52:
        return "Annually";
      case 0:
        return "Custom";
      default:
        return `Every ${frequency} weeks`;
    }
  };

  export enum ProjectEditEventSections {
    SectionAudience = 1,
    SectionAwareness = 2,
    SectionFollowup = 3,
    SectionInsideLoop = 4,
    SectionTestOptions = 5,
    SectionAddons = 6,
    SectionConfiguration = 7,
    SectionFrameworks = 8,
    SectionPlan = 9,
    SectionAudiences = 10,
  }

  export enum ProjectEditEventTypes {
    UserComment = 1,
    AiSuggestion = 2,
  }
  export enum ProjectEditModificationTypes {
    Add = 1,
    Edit = 2,
    Remove = 3,
  }

  export const UserResearchSetupConfidenceLevels = {
    1: "I need support end to end and don't feel confident choosing methods or writing questions on my own.",
    2: "I can create simple tests or questions, but need guidance on methods and improving them.",
    3: "I can set up studies and choose methods, though I still rely on the team for checks or verification.",
    4: "I can plan and run studies independently, but sometimes need help refining question wording.",
    5: "I confidently create tests end to end, from choosing methodologies, to creating questions and flows.",
  } as const;

  export const UserResearchInterpretationConfidenceLevels = {
    1: "I'm not sure how to read or interpret research results.",
    2: "I can understand results with guidance or explanation.",
    3: "I can read results and spot the main patterns.",
    4: "I can interpret findings and turn them into decisions.",
    5: "I analyse results confidently and explain insights clearly.",
  } as const;

  export const LaunchTestStepDescriptions: { [standardQuestionId: number]: string } = {
    [Globals.StandardQuestions.NPDAware]: "Qualified respondents who recognised the product",
    [Globals.StandardQuestions.InstoreVisibility]: "Qualified respondents who saw it in store",
    [Globals.StandardQuestions.NPDTried]: "Qualified respondents who tried the product",
    [Globals.StandardQuestions.Experience]: "Trialists who had a positive experience",
    [Globals.StandardQuestions.Repeat]: "Trialists who purchased it again",
  };

  export enum SentimentScores {
    Negative = -1,
    Neutral = 0,
    Positive = 1,
  }
}
